import { useTranslation } from "react-i18next";
import { Card, Col, Row, Space, Table, TableColumnProps } from "antd";
import { useEffect, useState } from "react";
import { ApiService, GetMyRequests } from "../../api";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import { Icon } from "@iconify/react";

interface RequestByStatusesProps {
  title: string;
  data: any[];
}

const RequestByStatuses = (props: RequestByStatusesProps) => {
  const { t } = useTranslation();
  const data = props.data.map((d) => ({
    name: d.status_name == null ? "N/A" : d.status_name,
    y: d.request_count,
  }));

  console.log(data);
  const options = {
    chart: {
      type: "pie",
      options3d: {
        enabled: true,
        alpha: 45,
      },
    },
    title: {
      text: props.title,
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: false,
        },
        showInLegend: true,
      },
    },
    series: [
      {
        name: t("Requests count"),
        data: data,
      },
    ],
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

interface RequestCountByTemplateProps {
  data?: Array<any>[];
}
const RequestCountByTemplate = (props: RequestCountByTemplateProps) => {
  const { t } = useTranslation();

  const options = {
    chart: {
      type: "column",
    },
    title: {
      text: "",
    },
    xAxis: {
      categories: [t("Type of requests")],
    },
    yAxis: {
      min: 0,
      title: { text: t("Number of requests") },
    },
    series: props.data,
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};


type RoundedSquareProps = {
  title: string;
  value: number;
  icon: string;
  color?: string;
};

const RoundedSquare = (props: RoundedSquareProps) => {
  return (
    <Card
      style={{
        borderRadius: 10,
        padding: "10px",
        textAlign: "center",
        borderWidth: "1px 1.5px 3px 1.5px",
      }}
      size="small"
    >
      <p style={{ fontSize: "18px", fontWeight: "bold" }}>{props.title}</p>
      <Row justify="center" align="middle">
        <Col span={12}>
          <Row justify="center" align="middle">
            <Icon
              icon={props.icon}
              color={props.color}
              style={{ width: "48px", height: "48px" }}
            />
            <h2 style={{ fontSize: "36px", margin: "0 8px" }}>{props.value}</h2>
          </Row>
        </Col>
      </Row>
    </Card>
  );
};

type TotalCounts = {
  total_request_count: number;
  total_user_count: number;
};

const DashboardPage = () => {
  const { t } = useTranslation();
  const columns: TableColumnProps<GetMyRequests>[] = [
    {
      title: t("ID"),
      dataIndex: "id",
    },
    {
      title: t("Template"),
      render: (_, record) => (
        <Space size="middle">{record.template?.short_name}</Space>
      ),
    },
    {
      title: t("Description"),
      dataIndex: "description",
    },
    {
      title: t("Actions"),
      render: (_, record) => (
        <Space size="middle">
          <a href={`/requests/${record.id}`}>{t("View")}</a>
        </Space>
      ),
    },
  ];
  const [templateCounts, setTemplateCounts] = useState<any>([]);
  const [statusCounts, setStatusCounts] = useState<any>([]);
  const [requests, setRequests] = useState<Array<GetMyRequests>>([]);
  const [loading, setLoading] = useState(false);
  const [totalCounts, setTotalCounts] = useState<TotalCounts>();

  useEffect(() => {
    (async () => {
      setLoading(true);
      await ApiService.apiMainStatusRequestCountRetrieve().then((result) => {
        setStatusCounts(result);
      });

      await ApiService.apiMainTemplateRequestCountRetrieve().then((result) => {
        const templateCounts = result.map((s: any) => {
          return { name: s.template_name, data: [s.request_count] };
        });
        setTemplateCounts(templateCounts);
      });

      await ApiService.apiMainLast10RequestsList().then((result) => {
        setRequests(result);
      });

      await ApiService.apiMainTotalCountsRetrieve().then((result) => {
        setTotalCounts(result);
      });

      setLoading(false);
    })();
  }, []);

  return (
    <div>
      <h2>{t("Dashboard Page")}</h2>
      <Row gutter={16}>
        <Col span={6}>
          <RoundedSquare
            title={t("Total requests")}
            value={totalCounts?.total_request_count!}
            icon="flat-color-icons:list"
          />
        </Col>
        <Col span={6}>
          <RoundedSquare
            title={t("Expired requests")}
            value={7}
            icon="flat-color-icons:expired"
          />
        </Col>
        <Col span={6}>
          <RoundedSquare
            title={t("Total Users")}
            value={totalCounts?.total_user_count!}
            icon="mdi:user"
            color="#4267b2"
          />
        </Col>
        <Col span={6}>
          <RoundedSquare
            title={t("Active Users")}
            value={1}
            icon="mdi:account-online"
            color="#4267b2"
          />
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <h3>{t("Request count by template")}</h3>

          <RequestCountByTemplate data={templateCounts} />
        </Col>
        <Col xs={24} sm={16}>
          <h3>{t("Recent activities")}</h3>

          <Table
            bordered
            size="small"
            dataSource={requests}
            columns={columns}
            loading={loading}
            rowKey={(record) => record.id}
            pagination={false}
          />
        </Col>
      </Row>
      <br />
      <Card title={t("Requests quantity by statuses")}>
        <Row gutter={[16, 16]}>
          {statusCounts.map((sc: any) => (
            <Col xs={24} sm={8}>
              <RequestByStatuses data={sc.data} title={sc.template_name} />
            </Col>
          ))}
        </Row>
      </Card>
    </div>
  );
};

export default DashboardPage;
